import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import PublishRounded from '@mui/icons-material/PublishRounded';
import UnpublishedRounded from '@mui/icons-material/UnpublishedRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import { Link as RouterLink } from 'react-router';
import { useNotify } from '@/components/feedback/notificationsContext';
import { fmt } from '@/features/notas/calculo';
import { CriteriosView, TabelaOrigem } from '@/features/notas/correcao';
import {
  buscaPublicacoes,
  publica,
  type DisciplinaPublicacao,
  type GrupoPublicacao,
  type StatusPublicacao,
} from '@/features/notas/service';
import { dataBr, nomeProprio } from '@/features/notas/texto';
import { rotuloSemestre } from '@/features/notas/types';
import { useAsync } from '@/hooks/useAsync';

function Status({ s }: { s: StatusPublicacao | null }) {
  return s?.aprovado ? (
    <Chip size="small" color="success" icon={<CheckCircleRounded />} label="No ar" />
  ) : (
    <Chip size="small" color="warning" variant="outlined" label="Aguardando sua aprovação" />
  );
}

/**
 * Tudo o que depende do professor antes de os alunos verem: o semestre, os critérios de cada disciplina e a correção
 * de cada grupo. Aprova (ou retira) um grupo, uma disciplina inteira ou tudo de uma vez.
 */
export function PublicacaoAba({ semestre }: { semestre: string }) {
  const notify = useNotify();
  const { data, error, loading, reload } = useAsync(
    (sig) => buscaPublicacoes(semestre, sig),
    [semestre],
  );
  const [ocupado, setOcupado] = useState<string | null>(null);

  const age = async (chave: string, dados: Parameters<typeof publica>[0], msg: string) => {
    setOcupado(chave);
    try {
      await publica(dados);
      notify(msg);
      reload();
    } catch {
      notify('Não deu certo. Tente de novo.');
    } finally {
      setOcupado(null);
    }
  };

  if (error) return <Alert severity="error">Não consegui carregar as publicações.</Alert>;
  if (loading && !data) return <CircularProgress />;
  if (!data) return null;

  const pendentes = data.disciplinas.reduce(
    (n, d) =>
      n +
      (d.criterios && !d.criteriosStatus?.aprovado ? 1 : 0) +
      d.grupos.filter((g) => !g.status?.aprovado).length,
    0,
  );

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
          >
            <Box>
              <Typography variant="h6" component="h2">
                Semestre {rotuloSemestre(semestre)}{' '}
                {data.semestre.publicado ? (
                  <Chip
                    size="small"
                    color="success"
                    label="visível para os alunos"
                    sx={{ ml: 1 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="ainda não aparece para os alunos"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pendentes
                  ? `${pendentes} item(ns) aguardando a sua aprovação.`
                  : 'Tudo deste semestre está aprovado.'}
                {data.semestre.publicado_em &&
                  ` Publicado em ${dataBr(data.semestre.publicado_em, true)}.`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<CheckCircleRounded />}
                disabled={!pendentes || ocupado !== null}
                onClick={() =>
                  void age(
                    'tudo',
                    { semestre, escopo: 'tudo', aprovado: true },
                    'Tudo aprovado: critérios e correções de todas as disciplinas.',
                  )
                }
              >
                Aprovar tudo
              </Button>
              <Button
                variant="outlined"
                color={data.semestre.publicado ? 'warning' : 'primary'}
                startIcon={data.semestre.publicado ? <UnpublishedRounded /> : <PublishRounded />}
                disabled={ocupado !== null}
                onClick={() =>
                  void age(
                    'semestre',
                    { semestre, escopo: 'semestre', aprovado: !data.semestre.publicado },
                    data.semestre.publicado
                      ? 'Semestre escondido dos alunos.'
                      : 'Semestre publicado para os alunos.',
                  )
                }
              >
                {data.semestre.publicado ? 'Esconder semestre' : 'Publicar semestre'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {data.disciplinas.map((d) => (
        <DisciplinaBloco key={d.codigo} d={d} semestre={semestre} ocupado={ocupado} age={age} />
      ))}
    </Stack>
  );
}

function DisciplinaBloco({
  d,
  semestre,
  ocupado,
  age,
}: {
  d: DisciplinaPublicacao;
  semestre: string;
  ocupado: string | null;
  age: (chave: string, dados: Parameters<typeof publica>[0], msg: string) => Promise<void>;
}) {
  const aprovados = d.grupos.filter((g) => g.status?.aprovado).length;
  const tudoOk = (!d.criterios || d.criteriosStatus?.aprovado) && aprovados === d.grupos.length;
  if (d.somenteNotas) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {d.codigo} · {d.nome} ({d.periodo})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Só P1, P2 e P3 (planilha do SIGA): não há critérios nem correção por grupo para aprovar.
            Aparece quando o semestre for publicado.
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return (
    <Accordion
      disableGutters
      variant="outlined"
      sx={{ '&:before': { display: 'none' }, borderRadius: 3 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' }, width: '100%', pr: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
            {d.codigo} · {d.nome} ({d.periodo})
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={`Critérios: ${d.criteriosStatus?.aprovado ? 'no ar' : d.criterios ? 'pendente' : '–'}`}
          />
          <Chip
            size="small"
            variant="outlined"
            color={tudoOk ? 'success' : 'warning'}
            label={`Grupos no ar: ${aprovados}/${d.grupos.length}`}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              disabled={tudoOk || ocupado !== null}
              onClick={() =>
                void age(
                  d.codigo,
                  { semestre, escopo: 'disciplina', disciplina: d.codigo, aprovado: true },
                  `${d.codigo}: critérios e todos os grupos aprovados.`,
                )
              }
            >
              Aprovar a disciplina inteira
            </Button>
            <Button
              size="small"
              color="warning"
              disabled={ocupado !== null}
              onClick={() =>
                void age(
                  d.codigo,
                  { semestre, escopo: 'disciplina', disciplina: d.codigo, aprovado: false },
                  `${d.codigo}: retirado do ar.`,
                )
              }
            >
              Retirar a disciplina do ar
            </Button>
          </Stack>

          {d.criterios && (
            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}
                  useFlexGap
                >
                  <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
                    Critérios da correção (como o aluno vai ver)
                  </Typography>
                  <Status s={d.criteriosStatus} />
                  <Button
                    size="small"
                    variant={d.criteriosStatus?.aprovado ? 'text' : 'contained'}
                    color={d.criteriosStatus?.aprovado ? 'warning' : 'primary'}
                    disabled={ocupado !== null}
                    onClick={() =>
                      void age(
                        `${d.codigo}-crit`,
                        {
                          semestre,
                          escopo: 'criterios',
                          disciplina: d.codigo,
                          aprovado: !d.criteriosStatus?.aprovado,
                        },
                        d.criteriosStatus?.aprovado
                          ? 'Critérios retirados do ar.'
                          : 'Critérios aprovados.',
                      )
                    }
                  >
                    {d.criteriosStatus?.aprovado ? 'Retirar' : 'Aprovar critérios'}
                  </Button>
                </Stack>
                <CriteriosView c={d.criterios} expandido />
              </CardContent>
            </Card>
          )}

          <Typography variant="h6" component="h3">
            Correção de cada grupo
          </Typography>
          {d.grupos.map((g) => (
            <GrupoBloco
              key={g.numero}
              d={d}
              g={g}
              semestre={semestre}
              ocupado={ocupado}
              age={age}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function GrupoBloco({
  d,
  g,
  semestre,
  ocupado,
  age,
}: {
  d: DisciplinaPublicacao;
  g: GrupoPublicacao;
  semestre: string;
  ocupado: string | null;
  age: (chave: string, dados: Parameters<typeof publica>[0], msg: string) => Promise<void>;
}) {
  const linhas = d.requisitos.map((rq) => {
    const r = g.requisitos?.find((x) => x.id === rq.id);
    return {
      id: rq.id,
      nome: rq.nome,
      secao: rq.secao,
      peso: rq.peso,
      nota: r?.nota ?? null,
      status: r?.status ?? null,
      justificativa: r?.justificativa ?? null,
    };
  });
  const chave = `${d.codigo}-${g.numero}`;
  return (
    <Accordion disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' }, width: '100%', pr: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">
              Grupo {g.numero} · nota {fmt(g.nota)}
              {g.fora ? ' · fora do prazo' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {g.integrantes.map(nomeProprio).join(', ')}
            </Typography>
          </Box>
          <Status s={g.status} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Button
              size="small"
              variant={g.status?.aprovado ? 'text' : 'contained'}
              color={g.status?.aprovado ? 'warning' : 'primary'}
              disabled={ocupado !== null}
              onClick={() =>
                void age(
                  chave,
                  {
                    semestre,
                    escopo: 'grupo',
                    disciplina: d.codigo,
                    grupo: g.numero,
                    aprovado: !g.status?.aprovado,
                  },
                  g.status?.aprovado
                    ? `Grupo ${g.numero} retirado do ar.`
                    : `Grupo ${g.numero} aprovado.`,
                )
              }
            >
              {g.status?.aprovado ? 'Retirar do ar' : 'Aprovar este grupo'}
            </Button>
            {g.ras[0] && (
              <Button
                size="small"
                startIcon={<VisibilityRounded />}
                component={RouterLink}
                to={`/admin/aluno/${g.ras[0]}/disciplina/${d.codigo}?semestre=${semestre}`}
              >
                Ver como o aluno vê
              </Button>
            )}
            {g.repositorio && (
              <Link
                href={g.repositorio}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ wordBreak: 'break-all' }}
              >
                {g.repositorio}
              </Link>
            )}
            {g.status && (
              <Typography variant="caption" color="text.secondary">
                {g.status.aprovado ? 'aprovado' : 'retirado'} por {g.status.atualizado_por} em{' '}
                {dataBr(g.status.atualizado_em, true)}
              </Typography>
            )}
          </Stack>
          <TabelaOrigem linhas={linhas} foraDoPrazo={g.fora} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
